import { CHANNEL_ID, LOG_ID } from '@@types/common';
import { AllLog, ReactionsLog } from '@@types/Log';
import { Reaction } from '@@types/Log/Reaction';

import ChannelLogsStore, { channelLogsStore } from '~/stores/ChannelLogsStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { threadChannelLogsStore } from '~/stores/ThreadChannelLogsStore';
import { runInAction } from 'mobx';
import { omit } from 'ramda';

class ReactionMessageHandler {
  public handleUpdate = (params: Reaction & { channelId: CHANNEL_ID; parentChannelId?: CHANNEL_ID; logId: LOG_ID }) => {
    runInAction(() => {
      const logStore = params.parentChannelId ? threadChannelLogsStore : channelLogsStore;

      const log = logStore.getById(params.logId);
      if (!log) {
        this.updateThreadChannelReaction(params);
        return;
      }
      if (
        params.channelId === mainDetailUiState.contentsChannelId ||
        params.channelId === threadDetailUiState.contentsChannelId
      ) {
        this.handleUpdateReaction(params, logStore, logStore.updateLog);
      }
      this.updateThreadChannelReaction(params);
    });
  };

  private updateThreadChannelReaction = (
    params: Reaction & { channelId: CHANNEL_ID; parentChannelId?: CHANNEL_ID; logId: LOG_ID }
  ) => {
    if (params.parentChannelId) {
      return;
    }
    if (params.logId === threadChannelLogsStore.threadPanelParentLogId) {
      this.handleUpdateReaction(params, threadChannelLogsStore, threadChannelLogsStore.updateParentLog);
    }
  };

  private scrollToBottomIfNeed = (
    log: ReactionsLog,
    params: Reaction & { channelId: CHANNEL_ID; parentChannelId?: CHANNEL_ID; logId: LOG_ID },
    logStore: ChannelLogsStore
  ) => {
    const hasReaction = 'reactions' in log && log.reactions.length > 0;
    if (logStore.lastLog?.id === params.logId && !hasReaction && params.members.length > 0) {
      params.parentChannelId ? threadDetailUiState.scrollToBottom() : mainDetailUiState.scrollToBottom();
    }
  };

  private handleUpdateReaction = (
    params: Reaction & { channelId: CHANNEL_ID; parentChannelId?: CHANNEL_ID; logId: LOG_ID },
    logStore: ChannelLogsStore,
    updateLog: (log: Partial<AllLog>) => void
  ) => {
    const isScrollBottom = params.parentChannelId
      ? threadDetailUiState.scrollAreaInfo.isScrollBottom
      : mainDetailUiState.scrollAreaInfo.isScrollBottom;
    this.updateReactionData(params, logStore);
    const log: ReactionsLog = logStore.getById(params.logId) as ReactionsLog;
    if (log) {
      const idx = log.reactions?.findIndex((reactionId) => reactionId === params.id) ?? -1;
      if (params.members.length === 0 && idx > -1) {
        updateLog({
          id: params.logId,
          reactions: [...log.reactions.slice(0, idx), ...log.reactions.slice(idx + 1)],
        });
      } else if (params.members.length > 0 && idx === -1) {
        updateLog({
          id: params.logId,
          reactions: [...(log.reactions ?? []), params.id],
        });
      }
      if (isScrollBottom) {
        this.scrollToBottomIfNeed(log, params, logStore);
      }
    }
  };

  private updateReactionData = (
    params: Reaction & { channelId: CHANNEL_ID; parentChannelId?: CHANNEL_ID; logId: LOG_ID },
    logStore: ChannelLogsStore
  ) => {
    const reaction: Reaction = omit(['channelId', 'logId'], params);
    if (reaction.members.length === 0) {
      logStore.removeReaction(reaction.id);
    } else {
      logStore.setReaction(reaction.id, reaction);
    }
  };
}

export default ReactionMessageHandler;
